import {evaluationIsVariant} from '@homeaway/react-experiment';
import {createSelector} from 'reselect';
import {getIsMobile} from './system';
import {getTravelerSegments} from './travelerSegmentFeatures';
import {getMultiUnitsLayoutValues} from './multiUnits';
import {
    VRBO_WEB_PDP_SPACES,
    VRBO_WEB_PDP_SUMMARY,
    VRBO_WEB_PDP_OPTIMIZE_AMENITIES
} from '../abacus/experiments';
import {
    getAbacusTests,
    getTranslations,
    getDynamicLayout as getDynamicLayoutRawData
} from './store';
import getAvailabilityCalendar from './availabilityCalendar';
import {SECTIONS, BLOCKS} from '../constants/layout';
import {generateLayoutOrder, parseMabResponse} from '../utils/layoutHelper';

export const getRoomsAndSpacesTestOn = createSelector(
    [getAbacusTests],
    (abacusTests) => {
        return evaluationIsVariant(abacusTests[VRBO_WEB_PDP_SPACES], 1);
    }
);

export const getListingSummaryTestOn = createSelector(
    [getAbacusTests],
    (abacusTests) => {
        return evaluationIsVariant(abacusTests[VRBO_WEB_PDP_SUMMARY], 1);
    }
);

export const getAmenitiesTestOn = createSelector(
    [getAbacusTests],
    (abacusTests) => {
        return evaluationIsVariant(abacusTests[VRBO_WEB_PDP_OPTIMIZE_AMENITIES], 1);
    }
);

const getIsDynamicLayout = createSelector(
    [getDynamicLayoutRawData],
    (dynamicLayoutRawData) => !!dynamicLayoutRawData
);

export const getNavLabels = createSelector(
    [getTranslations, getAvailabilityCalendar, getMultiUnitsLayoutValues, getListingSummaryTestOn],
    (trans, availabilityCalendar, multiUnitsLayoutValues, listingSummaryTestOn) => {
        return {
            [SECTIONS.OVERVIEW]: listingSummaryTestOn ? trans.navAbout : trans.navOverview,
            [SECTIONS.AMENITIES]: trans.navAmenities,
            [SECTIONS.ROOMS_AND_SPACES]: trans.navRoomsAndBeds,
            [SECTIONS.REVIEWS]: trans.navReviews,
            [SECTIONS.MAP]: trans.navMap,
            [SECTIONS.RATES_AVAILABILITY]: availabilityCalendar.shouldShowRates
                ? trans.navRatesAvailability
                : trans.navAvailability,
            [SECTIONS.OTHER_UNITS]: multiUnitsLayoutValues.isHotelType
                ? trans.navRooms
                : trans.navUnits,
            [SECTIONS.POLICIES]: trans.navPolicies,
            [SECTIONS.HOST]: trans['internal-components.navigation-bar.host']
        };
    }
);

const getSectionsConfig = createSelector(
    [getIsMobile, getListingSummaryTestOn, getNavLabels, getTranslations, getIsDynamicLayout],
    (isMobile, listingSummaryTestOn, navLabels, translations, isDynamicLayout) => ({
        [SECTIONS.OVERVIEW]: {
            navLabel: navLabels[SECTIONS.OVERVIEW],
            blocks: isMobile
                ? [
                      BLOCKS.INLINE_GLOBAL_MESSAGE,
                      !listingSummaryTestOn && BLOCKS.LISTING_SUMMARY,
                      listingSummaryTestOn && BLOCKS.ABOUT_THE_PROPERTY,
                      BLOCKS.TRIP_DETAILS,
                      BLOCKS.FREE_CANCELLATION,
                      BLOCKS.OWNER_DETAILS_MINI,
                      BLOCKS.BOOKING_ASSISTANCE,
                      BLOCKS.BEAR_STAY_BANNER,
                      !listingSummaryTestOn && BLOCKS.ABOUT_THE_PROPERTY,
                      BLOCKS.CIVIL_CODE_DISCLAIMER
                  ]
                : [
                      BLOCKS.INLINE_GLOBAL_MESSAGE,
                      !listingSummaryTestOn && BLOCKS.LISTING_SUMMARY,
                      listingSummaryTestOn && BLOCKS.ABOUT_THE_PROPERTY,
                      BLOCKS.TRIP_DETAILS,
                      !listingSummaryTestOn && BLOCKS.ABOUT_THE_PROPERTY
                  ]
        },
        [SECTIONS.INLINE_VIRTUAL_TOUR]: {
            blocks: [BLOCKS.INLINE_VIRTUAL_TOUR]
        },
        [SECTIONS.UNIT_SPACES]: {
            blocks: [BLOCKS.UNIT_SPACES]
        },
        [SECTIONS.MIDPAGE_RECO_CAROUSEL]: {
            blocks: [BLOCKS.MIDPAGE_RECO_CAROUSEL]
        },
        [SECTIONS.ROOMS_AND_SPACES]: {
            navLabel: navLabels[SECTIONS.ROOMS_AND_SPACES],
            blocks: [BLOCKS.ROOMS_AND_SPACES, isDynamicLayout && BLOCKS.INLINE_VIRTUAL_TOUR]
        },
        [SECTIONS.AMENITIES]: {
            navLabel: navLabels[SECTIONS.AMENITIES],
            blocks: [BLOCKS.AMENITIES]
        },
        [SECTIONS.POLICIES]: {
            navLabel: navLabels[SECTIONS.POLICIES],
            sectionTitle: translations.navPolicies,
            blocks: [
                BLOCKS.CANCELLATION_POLICY,
                isMobile && BLOCKS.TRIP_LINK,
                BLOCKS.DAMAGE_POLICY,
                BLOCKS.HOUSE_RULES,
                BLOCKS.CLEANING_PRACTICES
            ]
        },
        [SECTIONS.OTHER_UNITS]: {
            navLabel: navLabels[SECTIONS.OTHER_UNITS],
            blocks: [BLOCKS.OTHER_UNITS]
        },
        [SECTIONS.REVIEWS]: {
            navLabel: navLabels[SECTIONS.REVIEWS],
            blocks: [BLOCKS.REVIEWS]
        },
        [SECTIONS.MAP]: {
            navLabel: navLabels[SECTIONS.MAP],
            blocks: [BLOCKS.MAP]
        },
        [SECTIONS.CHAT_UI]: {
            blocks: [BLOCKS.CHAT_UI]
        },
        [SECTIONS.HOST]: {
            navLabel: navLabels[SECTIONS.HOST],
            blocks: [isMobile && BLOCKS.CHAT_UI, BLOCKS.OWNER_DETAILS, BLOCKS.AUTH_CARD]
        },
        [SECTIONS.RATES_AVAILABILITY]: {
            navLabel: navLabels[SECTIONS.RATES_AVAILABILITY],
            blocks: [BLOCKS.RATES_AVAILABILITY_CALENDAR]
        }
    })
);

// Returns the default layout sections ordered
const getDefaultLayout = createSelector(
    [
        getIsMobile,
        getTravelerSegments,
        getMultiUnitsLayoutValues,
        getRoomsAndSpacesTestOn,
        getAmenitiesTestOn,
        getIsDynamicLayout
    ],
    (
        isMobile,
        {coldStartUser},
        {showMultiUnits, isHotelType},
        roomsAndSpacesTestOn,
        amenitiesTestOn,
        isDynamicLayout
    ) => {
        const showMidPageCarousel = coldStartUser && !showMultiUnits;

        return [
            SECTIONS.OVERVIEW,
            !isDynamicLayout && SECTIONS.INLINE_VIRTUAL_TOUR,
            isMobile && showMidPageCarousel && SECTIONS.MIDPAGE_RECO_CAROUSEL,
            !roomsAndSpacesTestOn && SECTIONS.UNIT_SPACES,
            !isMobile && showMidPageCarousel && SECTIONS.MIDPAGE_RECO_CAROUSEL,
            roomsAndSpacesTestOn && SECTIONS.ROOMS_AND_SPACES,
            isMobile ? amenitiesTestOn && SECTIONS.AMENITIES : SECTIONS.AMENITIES,
            SECTIONS.POLICIES,
            showMultiUnits && isHotelType && SECTIONS.OTHER_UNITS,
            SECTIONS.REVIEWS,
            SECTIONS.MAP,
            !isMobile && SECTIONS.CHAT_UI,
            SECTIONS.HOST,
            SECTIONS.RATES_AVAILABILITY,
            isMobile && !amenitiesTestOn && SECTIONS.AMENITIES,
            showMultiUnits && !isHotelType && SECTIONS.OTHER_UNITS
        ];
    }
);

export const getDynamicLayout = createSelector(
    [getDynamicLayoutRawData],
    (dynamicLayoutRawData) => {
        // If throttle is OFF, this will be null
        if (!dynamicLayoutRawData) {
            return [];
        }

        // If data available in reducer, throttle is ON and it successfully fetched data from MAB
        // Here happens the parsing logic, append/preppend about the property and other sections that are not part of MAB response
        // Returns array with the sections properly ordered or an empty array if the parsing fails for any reason
        const parsed = parseMabResponse(dynamicLayoutRawData);

        if (parsed.length) {
            return [SECTIONS.OVERVIEW, ...parsed];
        }

        return [];
    }
);

const getLayout = createSelector(
    [getDynamicLayout, getDefaultLayout, getSectionsConfig],
    (dynamicLayout, defaultLayout, sectionsConfig) => {
        const isDynamicLayout = !!dynamicLayout.length;
        const layoutOrder = isDynamicLayout ? dynamicLayout : defaultLayout;

        return {
            order: generateLayoutOrder(layoutOrder, sectionsConfig)
        };
    }
);

export default getLayout;
export {getLayout, SECTIONS, BLOCKS};
